<template>
	<div class="block-editor-container subflow-block-editor">
		<StepDetailsHeader
			v-on="$listeners"
			hideActionText
			:icon="subflowServiceSlug"
			:hideProfileSelectionOption="!isProfileSelectionNeeded"
			:isContextualNode="false"
			:showDropdownMenu="false"
		/>
		<div class="editor-body flex-stretch-to-scroll pa-4 font-size-md">
			<div class="flex-space-between--center">
				<div class="font-weight-600">Output fields</div>
				<OutlinedButton v-if="subflowOutput?.length > 0" class="edit-button" small @click="editFields">Edit output fields</OutlinedButton>
			</div>

			<div class="mt-2 mb-4">
				<div>Output fields let you return values to the parent feature.</div>
				<div>1. Add output fields</div>
				<div>2. Map data into the fields</div>
			</div>
			<SubFlowParamsForm
				:fieldsDefinitions="subflowOutput"
				:node="editNode"
				@updateSubflowParams="updateSubflowParams"
				@setIsValid="setIsValid"
			/>

			<OutlinedButton v-if="subflowOutput?.length === 0" class="add-button mt-5" @click="editFields">+ Add output field</OutlinedButton>
		</div>
		<BottomSection :testResponseTruncate="testResponseTruncate" :isFormValid="isFormValid" />
	</div>
</template>

<script>
import { mapActionsShorthand, mapGettersShorthand } from '@/store/utils/mappers';
import { editWorkflow } from '@/store/modules/selectedWorkflow/getters';
import { editNode } from '@/store/modules/selectedNode/getters';
import { setParamInEditNode } from '@/store/modules/selectedNode/actions';
import { getSubflowParams } from '@/store/modules/subflows/getters';
import {
	fetchSubflowParam,
	updateSubflowParam,
	deleteParamsMapParamsFromAllEndNodes,
	setNeedToTestAllNodes,
} from '@/store/modules/subflows/actions';

import BlockEditorBridge from '../block-editor-bridge';

import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import TestModalService from '../helpers/test-modal-service';

import StepDetailsHeader from '../StepDetailsHeader';
import BottomSection from './helpers/BottomSection';
import SubFlowParamsForm from './helpers/SubFlowParamsForm';
import { ServiceSlugs } from '@ply-io/ply-front-shared-utilities';

export default {
	name: 'SubflowOutputBlockEditor',
	components: {
		StepDetailsHeader,
		SubFlowParamsForm,
		BottomSection,
	},
	props: {
		nodeID: { type: [Number, String] },
	},
	data() {
		return {
			isShowParams: null,
			isShowTestResultModal: false,
			isShowAnimation: false,
			isTestedDataChanged: false,
			isBtnFuncLoading: false,
			isWaitingForSampleData: false,
			isChangeAction: false,
			isTriggerSetupLoading: false,
			hasTestParams: false,
			timer: null,
			isFormValid: true,
		};
	},
	computed: {
		...mapGettersShorthand({ editNode, editWorkflow, getSubflowParams }),
		stepHeaderIcon() {
			return BlockEditorBridge.stepHeaderIcon();
		},
		isProfileSelectionNeeded() {
			return BlockEditorBridge.isProfileSelectionNeeded();
		},
		subflowOutputId() {
			return this.editWorkflow.current_draft.sub_flow_output_configuration;
		},
		subflowOutput() {
			return this.editWorkflow && this.getSubflowParams(this.subflowOutputId);
		},
		subflowServiceSlug() {
			return ServiceSlugs.SubFlow;
		},
	},
	methods: {
		...mapActionsShorthand({
			fetchSubflowParam,
			updateSubflowParam,
			deleteParamsMapParamsFromAllEndNodes,
			setNeedToTestAllNodes,
			setParamInEditNode,
		}),
		editFields() {
			const { subflowOutput } = this;

			const onSave = (newFieldDef) => {
				const newUuids = newFieldDef.map((field) => field.uuid);
				const subflowOutputUuids = subflowOutput.map((output) => output.uuid);
				const deletedUuids = subflowOutputUuids.filter((uuid) => !newUuids.includes(uuid));
				const addedUuids = newUuids.filter((uuid) => !subflowOutputUuids.includes(uuid));

				this.deleteParamsMapParamsFromAllEndNodes({ deletedParams: deletedUuids });
				this.updateSubflowParam({ id: this.subflowOutputId, subflowParams: newFieldDef });

				if (addedUuids.length > 0) {
					this.setNeedToTestAllNodes();
					const hasNewRequiredFields = newFieldDef.filter((field) => addedUuids.includes(field.uuid)).some((def) => def.is_required);

					if (hasNewRequiredFields) {
						this.setIsValid(false);
					}
				}
			};

			AppDialogManagerService.openNewOutputBlockDialog({ onSave, fieldsDefinitions: subflowOutput, selectedSubflow: this.editWorkflow });
		},
		updateSubflowParams(newOutputParams) {
			this.setParamInEditNode({ key: 'params_map', value: newOutputParams, options: {} });
		},
		setIsValid(isFormValid) {
			this.$set(this, 'isFormValid', isFormValid);
		},
		testResponseTruncate(response) {
			return TestModalService.mapTestResponseToModalView(response, this.subflowOutput);
		},
	},
	mounted() {
		this.fetchSubflowParam({ id: this.subflowOutputId, forceGet: true });
	},
};
</script>

<style scoped lang="scss">
@import '@/components/WidgetForm/widget-form-mixins.scss';
@import '../block-editor.scss';

.editor-body {
	max-width: 470px;

	.add-button {
		width: 100%;
		border: 1px dashed var(--publish-button-background) !important;
		color: var(--publish-button-background) !important;
	}

	.edit-button {
		height: 21px;
		color: #5e5ee2 !important;
		border-color: #5e5ee2 !important;
		font-size: 14px !important;
		text-transform: none !important;
	}
}
</style>
